import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { useTheme } from "../../ThemeContext";
import { fetchAllCallsForRequest, fetchCallsList } from "../../services/call-history-service";
import CallDetailsHeader from "./components/CallDetailsHeader";
import CallTranscriptView from "./components/CallTranscriptView";
import CallSummaryView from "./components/CallSummaryView";
import { message } from "antd";
import "./CallDetailsPage.css";

/**
 * CallDetailsPage - Displays detailed call information with transcript and summary
 * Handles multiple call attempts (recordings) when they exist
 */
const CallDetailsPage = () => {
  console.log("CallDetailsPage component rendering...");
  
  const { currentTheme, themeMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const callRequestId = searchParams.get("call_request_id");
  const renderType = searchParams.get("renderType") || "summary"; // Get from URL
  
  console.log("CallDetailsPage - callRequestId:", callRequestId, "renderType:", renderType);

  // State
  const [loading, setLoading] = useState(true);
  const [allCalls, setAllCalls] = useState([]); // Array of all call attempts
  const [whitesheetData, setWhitesheetData] = useState(null);
  const [batchMetadata, setBatchMetadata] = useState(null); // eslint-disable-line no-unused-vars
  const [callRequestData, setCallRequestData] = useState(null);
  const [faxSignedUrl, setFaxSignedUrl] = useState(null); // Fax URL for popup
  const [selectedTab, setSelectedTab] = useState(renderType); // Initialize from URL
  const [currentAttemptIndex, setCurrentAttemptIndex] = useState(0);
  const [error, setError] = useState(null);

  // State for patient navigation
  const [callNavCache, setCallNavCache] = useState({ 
    calls: [], 
    page: 0, 
    rowsPerPage: 10, 
    totalCount: 0, 
    batchId: '',
    query: '', // Added query to state
    callStatus: '', // Added callStatus filter to state
  });
  const [successfulCallRequestIds, setSuccessfulCallRequestIds] = useState([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const [noMoreSuccessfulCallsForward, setNoMoreSuccessfulCallsForward] = useState(false);

  // Get current call based on selected attempt index
  const currentCall = allCalls[currentAttemptIndex] || null;

  // Update URL when tab changes
  const handleTabChange = useCallback((newTab) => {
    setSelectedTab(newTab);
    
    // Update URL with new tab, preserving call_request_id
    const params = new URLSearchParams();
    params.set("call_request_id", callRequestId);
    params.set("renderType", newTab);
    setSearchParams(params);
  }, [callRequestId, setSearchParams]);

  // Sync selectedTab with URL parameter (for browser back/forward)
  useEffect(() => {
    const urlTab = searchParams.get("renderType") || "summary";
    setSelectedTab(urlTab);
  }, [searchParams]);

  // Fetch all calls for the call request
  const loadCallDetails = useCallback(async () => {
    if (!callRequestId) return;

    try {
      setLoading(true);
      setError(null);
      const result = await fetchAllCallsForRequest(callRequestId);
      
      if (result.success) {
        setAllCalls(result.calls || []);
        setWhitesheetData(result.whitesheetData);
        setBatchMetadata(result.batchMetadata);
        setCallRequestData(result.callRequestData);
        setFaxSignedUrl(result.faxSignedUrl || null);
        
        // Default to the first call (most recent)
        setCurrentAttemptIndex(0);
      } else {
        setError(result.error || "Failed to load call details");
      }
    } catch (err) {
      console.error("Error loading call details:", err);
      setError("An error occurred while loading call details");
    } finally {
      setLoading(false);
    }
  }, [callRequestId]);

  useEffect(() => {
    loadCallDetails();

    // Reset forward navigation lock when call request changes
    setNoMoreSuccessfulCallsForward(false);
  }, [callRequestId, loadCallDetails]);

  // Load navigation cache from session storage
  useEffect(() => {
    const cachedData = sessionStorage.getItem('callHistoryNavCache');
    if (cachedData) {
      const parsedData = JSON.parse(cachedData);
      setCallNavCache(parsedData);

      console.log('callRequestIdsArray Original fetched array from cache:', parsedData.calls);
      
      const filteredCalls = parsedData.calls.filter(call => 
        call.callStatus === 'completed' && call.callOutcome === 'Successful'
      );
      console.log('callRequestIdsArray Filtered out calls (completed and successful):', filteredCalls);

      const filteredIds = filteredCalls.map(call => call.callRequestId);
      setSuccessfulCallRequestIds(filteredIds);
      
      // Keep the main indicator log for callRequestIdsArray
      console.log('callRequestIdsArray', filteredIds);
    }
  }, []);

  // Navigate back to list (clean URL without parameters)
  const handleBackToList = () => {

    
    const { page, rowsPerPage, batchId, query, callStatus } = callNavCache;
    const params = new URLSearchParams();
    if (batchId) params.set('batch_id', batchId);
    if (query) params.set('q', query); // Add search query back to URL
    if (callStatus) params.set('call_status', callStatus); // Add call_status filter back to URL
    params.set('page', page.toString());
    params.set('pageSize', rowsPerPage.toString());
    navigate(`/call-history?${params.toString()}`);
  };

  // Navigate to previous/next recording (attempt)
  const handlePreviousRecording = () => {
    if (currentAttemptIndex > 0) {
      setCurrentAttemptIndex(prev => prev - 1);
    }
  };

  const handleNextRecording = () => {
    const maxAttempts = allCalls.length;
    if (currentAttemptIndex < maxAttempts - 1) {
      setCurrentAttemptIndex(prev => prev + 1);
    }
  };

  const handleNextPatient = async () => {
    if (isNavigating) return;

    const currentIndex = successfulCallRequestIds.indexOf(callRequestId);
    if (currentIndex < successfulCallRequestIds.length - 1) {
      const nextId = successfulCallRequestIds[currentIndex + 1];
      navigate(`/call-history?call_request_id=${nextId}&renderType=summary`);
    } else {
      setIsNavigating(true);
      message.loading({
        content: "Searching for the next successful call...",
        key: "nextPatientSearch",
        duration: 0,
      });

      let { page, rowsPerPage, totalCount, batchId, query, callStatus } = callNavCache;
      let nextPageToFetch = page + 1;
      const maxPages = Math.ceil(totalCount / rowsPerPage);
      let foundNextPatient = false;

      while (nextPageToFetch < maxPages && !foundNextPatient) {
        const result = await fetchCallsList({
          batchId,
          page: nextPageToFetch,
          rowsPerPage,
          searchQuery: query,
          callStatus: callStatus,
        });

        if (result.calls.length > 0) {
          const newIds = result.calls
            .filter(
              (call) =>
                call.callStatus === "completed" &&
                call.callOutcome === "Successful"
            )
            .map((call) => call.callRequestId);

          if (newIds.length > 0) {
            setSuccessfulCallRequestIds((prev) => [...prev, ...newIds]);
            setCallNavCache((prev) => ({
              ...prev,
              calls: [...prev.calls, ...result.calls],
              page: nextPageToFetch,
            }));
            navigate(
              `/call-history?call_request_id=${newIds[0]}&renderType=summary`
            );
            foundNextPatient = true;
          }
        }
        if (!foundNextPatient) {
          nextPageToFetch++;
        }
      }

      message.destroy("nextPatientSearch");
      setIsNavigating(false);

      if (!foundNextPatient) {
        const endMessage = batchId 
          ? "You have reached the last successful call in this batch."
          : "You have reached the last successful call.";
        message.info(endMessage, 3);
        setNoMoreSuccessfulCallsForward(true);
      }
    }
  };

  const handlePreviousPatient = async () => {
    const currentIndex = successfulCallRequestIds.indexOf(callRequestId);
    if (currentIndex > 0) {
      const prevId = successfulCallRequestIds[currentIndex - 1];
      navigate(`/call-history?call_request_id=${prevId}&renderType=summary`);
    } else {
      // Fetch previous page if available
      const { page, rowsPerPage, batchId, query, callStatus } = callNavCache;
      const prevPage = page - 1;

      if (prevPage >= 0) {
        const result = await fetchCallsList({ batchId, page: prevPage, rowsPerPage, searchQuery: query, callStatus: callStatus });
        if (result.calls.length > 0) {
          const newIds = result.calls
            .filter(call => call.callStatus === 'completed' && call.callOutcome === 'Successful')
            .map(call => call.callRequestId)
            .reverse();
          
          if (newIds.length > 0) {
            setSuccessfulCallRequestIds(prev => [...newIds, ...prev]);
            setCallNavCache(prev => ({ ...prev, calls: [...result.calls, ...prev.calls], page: prevPage }));
            navigate(`/call-history?call_request_id=${newIds[0]}&renderType=summary`);
          }
        }
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 80px)",
          backgroundColor: currentTheme.callhistory?.mainBackground,
        }}
      >
        <img
          src={
            themeMode === "dark"
              ? "/icons/loader_white.gif"
              : "/icons/loader_black.gif"
          }
          alt="Loading..."
          style={{ width: "150px", height: "150px" }}
        />
      </Box>
    );
  }

  // Error state
  if (error || !currentCall) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 80px)",
          backgroundColor: currentTheme.callhistory?.mainBackground,
          gap: 2,
        }}
      >
        <Box sx={{ fontSize: "1.2rem", color: currentTheme.text.primary }}>
          {error || "No calls found"}
        </Box>
        <Box
          onClick={handleBackToList}
          sx={{
            px: 3,
            py: 1,
            backgroundColor: "#e63e2b",
            color: "#fff",
            borderRadius: "8px",
            cursor: "pointer",
            "&:hover": { backgroundColor: "#d32f2f" },
          }}
        >
          Back to Call List
        </Box>
      </Box>
    );
  }

  const totalAttempts = allCalls.length;
  
  // Get patient info from callRequestData
  const patientData = callRequestData?.patientData || {};
  const memberName = `${patientData['Member First Name'] || ""} ${patientData['Member Middle Name'] || ""} ${patientData['Member Last Name'] || ""}`.trim();
  const memberId = patientData['Primary Member ID'] || "N/A";
  const currentIndex = successfulCallRequestIds.indexOf(callRequestId);
  const isLastPatient = noMoreSuccessfulCallsForward && currentIndex === successfulCallRequestIds.length - 1;

  return (
    <Box
      className="call-details-page"
      sx={{
        width: "100%",
        minHeight: "calc(100vh - 80px)",
        backgroundColor: currentTheme.callhistory?.mainBackground,
        padding: 3,
      }}
    >
      {/* Header with patient info and navigation */}
      <CallDetailsHeader
        memberName={memberName || "Unknown Member"}
        memberId={memberId}
        onBack={handleBackToList}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        currentTheme={currentTheme}
        themeMode={themeMode}
        totalAttempts={totalAttempts}
        currentAttemptIndex={currentAttemptIndex}
        onPreviousRecording={handlePreviousRecording}
        onNextRecording={handleNextRecording}
        onNextPatient={handleNextPatient}
        onPreviousPatient={handlePreviousPatient}
        isFirstPatient={
          successfulCallRequestIds.indexOf(callRequestId) === 0 &&
          callNavCache.page === 0
        }
        isLastPatient={isLastPatient || isNavigating}
        faxSignedUrl={faxSignedUrl}
      />

      {/* Content Area */}
      <Box sx={{ mt: 2 }}>
        {selectedTab === "transcript" && (
          <CallTranscriptView
            callData={currentCall}
            callRequestId={callRequestId}
            currentTheme={currentTheme}
            themeMode={themeMode}
          />
        )}
        
        {selectedTab === "summary" && (
          <CallSummaryView
            callData={currentCall}
            whitesheetData={whitesheetData}
            patientData={patientData}
            currentTheme={currentTheme}
            themeMode={themeMode}
          />
        )}
      </Box>
    </Box>
  );
};

export default CallDetailsPage;

